import { useEffect, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { ButtonData } from '../../../../../components/buttonGroup/ButtonGroup';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useToast } from '../../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../../hooks';
import { Scopes, User } from '../../../../../services/endpoints/people/user';

import { useGetCountryFactsQuery } from '../../../../../services/endpoints/content/countryFacts';
import { CountryFact } from '../../../../../types/countryFacts.type';
import { countryFactsColumns } from '../../../../../utils/consts/columnDefs/countryFacts';
import { getFilterValue } from '../../../../../utils/listFilterSearchParams';

const defaultScope = 'all';

export const userCanEditCreateDeleteView_CountryFacts = (user: User | null) => {
  if (user?.isAdmin && user?.isSuper) {
    return true;
  }
  if (user?.isWebContentEditor || user?.isSupremeAdmin || user?.isGlobal) {
    return true;
  }

  return false;
};

export const CountryFactsView = () => {
  const { addToast } = useToast();
  const [filters, setFilters] = useListFiltersInUrl({
    scope: defaultScope,
    data: { page: 1, entries: 10 }
  });
  const [scopes, setScopes] = useState<Scopes>({});
  const { data, error, isLoading, isFetching } = useGetCountryFactsQuery(filters);
  const [countryFact, setThemes] = useState<CountryFact[]>([]);
  const [buttons, setButtons] = useState<ButtonData[][]>();
  const navigate: NavigateFunction = useNavigate();

  const filterFormFields: FilterField[] = [
    {
      name: 'name',
      label: 'Name',
      value: getFilterValue(filters.data?.filterBy, 'name')
    },
    {
      name: 'content',
      label: 'Content',
      value: getFilterValue(filters.data?.filterBy, 'content')
    }
  ];

  useEffect(() => {
    if (!error && !isLoading && data && data.results) {
      if (data.scopes) {
        setScopes(
          data.scopes.reduce((acc: Scopes, curr: Scopes) => ({ ...acc, ...curr }), {} as Scopes)
        );
        setButtons(
          data.scopes.map((sMap) =>
            Object.entries(sMap).map(([scope, title]) => {
              let scope_count = 0;
              if (data.scope_counts != null) {
                scope_count = data.scope_counts[scope] ?? 0;
              }
              return {
                title: `${title} (${scope_count})`,
                key: scope
              };
            })
          )
        );
      }
    } else if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setThemes(data.results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  return (
    <div className="py-2">
      <SFCTable
        name={`${scopes[filters?.scope!] ?? ''} Country Facts`}
        columns={countryFactsColumns}
        data={countryFact}
        count={data?.count}
        defaultScope={filters.scope ?? defaultScope}
        scopes={buttons}
        isLoading={isLoading}
        isFetching={isFetching}
        indexDownloadPath="country-facts"
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        onFiltersChanged={setFilters}
        newItemEl={
          <SimpleButton
            variant="sfc-blueberry"
            height={40}
            truncate={false}
            onClick={() => navigate('/admin/content/country-facts/new')}
          >
            New Country Fact
          </SimpleButton>
        }
      />
    </div>
  );
};
